'use client'

import httpClient from '@apiClient'
import useEnquiry from '@hooks/useEnquiry'
import useLogin from '@hooks/useLogin'
import { enquiryDataLayer } from '@plugin/google/gtm/modules/property'
import { getHouseEnquiryParams, getHouseId, isSaleData } from '@utils/house'
import classNames from 'classnames'
import { first, isFunction } from 'lodash-es'
import { Suspense, useCallback, useEffect, useRef } from 'react'
import { useAsyncFn, useBoolean, useLocalStorage } from 'react-use'
import RequestButton from '../openHomeLayout/requestButton'

export default function ContactButton({
  data,
  house,
  sendAllAgent = false,
  customHandler,
  successBoxId = '',
  enquiryType = ''
}) {

  const { isLogin } = useLogin()
  const isBuy = isSaleData(house)
  const { showHouseEnquiry } = useEnquiry()
  const requestHandle = useCallback(event => {
    enquiryDataLayer.click(data)
    showHouseEnquiry({
      house: house,
      agent: first(house.agents),
      signupSource: 'agent_enquiry',
      spec: data,
      sendAllAgent,
      enquiryType
    })(event)
  }, [showHouseEnquiry, data, house, sendAllAgent, enquiryType])

  const [contacted, setContacted] = useLocalStorage(`${getHouseId(house)}_${data.value}_contacted`, false)
  const aniRef = useRef()
  const timeInRef = useRef()
  const timeOutRef = useRef()
  const [sentSate, handleSent] = useAsyncFn(async () => {
    if (isFunction(customHandler)) {
      customHandler()
      return
    }

    if (contacted || !isLogin || !isBuy) {
      requestHandle()
      return
    }

    if (aniRef.current) aniRef.current.beginElement()
    clearTimeout(timeInRef.current)
    clearTimeout(timeOutRef.current)
    let user = JSON.parse(sessionStorage.getItem('enquiry-user'))
    if (!user) {
      user = await httpClient.get('user/info')
      sessionStorage.setItem('enquiry-user', JSON.stringify(user))
    }

    const params = await getHouseEnquiryParams(httpClient, data)(house, user)
    await httpClient.post('user/enquiry', params)
    enquiryDataLayer.success()
    if (aniRef.current) aniRef.current.endElement()

    // 延迟1.5s设置已联系
    await new Promise(resolve => setTimeout(resolve, 1000))
    setContacted(true)
    const agentSuccess = document.getElementById(successBoxId)
    timeInRef.current = setTimeout(() => {
      agentSuccess.style.opacity = 1
      agentSuccess.style.height = 'auto'
    }, 1500)
    timeOutRef.current = setTimeout(() => {
      agentSuccess.style.opacity = 0
      agentSuccess.style.height = 0
    }, 4000)
  }, [contacted, isLogin, isBuy, house, data, requestHandle, customHandler])

  return (
    <RequestButton onRequest={handleSent} disabled={sentSate.loading} className='flex items-center justify-center'>
      {
        (!isLogin || !isBuy)
          ? <i className='icon icon-arrow-right !text-lg'></i>
          : (
            <>
              <ButtonSent active={contacted} className='mr-10' />
              {
                !contacted && (
                  <svg width="36" height="36" viewBox="0 0 36 36"
                    xmlns="http://www.w3.org/2000/svg">
                    <g fill="none" fillRule="evenodd">
                      <path d="M34 18c0 8.836-7.164 16-16 16-8.837 0-16-7.164-16-16C2 9.163 9.163 2 18 2c8.836 0 16 7.163 16 16z" stroke="#D9D9D9" strokeWidth={sentSate.loading ? 3 : 0} strokeDasharray="113.097" />
                      <path d="M34 18c0 8.836-7.164 16-16 16-8.837 0-16-7.164-16-16C2 9.163 9.163 2 18 2c8.836 0 16 7.163 16 16z" stroke="#17A11A" strokeWidth={sentSate.loading ? 3 : 0} strokeDasharray="113.097" >
                        <animate attributeName="stroke-dashoffset" from="113.097" to="0" dur="3s" repeatCount="1" begin="indefinite" ref={aniRef} />
                      </path>
                      <path d="M20.876 15.124l-5.94 4.124-6.213-2.072a1.058 1.058 0 0 1 .01-2.01l15.885-5.115a1.059 1.059 0 0 1 1.331 1.331l-5.115 15.885a1.057 1.057 0 0 1-2.01.01l-2.082-6.244 4.134-5.91z" fill="#485696" fillRule="nonzero" />
                    </g>
                  </svg>
                )
              }
            </>
          )
      }
    </RequestButton>
  )
}

export function LinkEnquiryButton() {
  return (
    <Suspense>
      <ContactButton data={{}} house={{}} customHandler={() => { }} />
    </Suspense>
  )
}

function ButtonSent({
  className,
  active = false
}) {
  const [showAsk, setShowAsk] = useBoolean(active)

  useEffect(() => {
    if (!active) return
    setTimeout(() => setShowAsk(true), 3500)
  }, [setShowAsk, active])

  return (
    <div className={classNames(className, active ? '' : 'w-0 h-0 overflow-hidden')}>
      <div
        className={classNames(
          'transition-opacity duration-1000 bg-opacity-10 grid place-items-center rounded-[100px] bg-[#17A11A] text-[#17A11A]',
          active ? 'opacity-100' : 'opacity-0',
          showAsk ? 'w-0 h-0 overflow-hidden' : 'w-90 h-32'
        )}
      >
        Sent
      </div>
      <div
        className={classNames(
          'transition-opacity duration-1000 bg-opacity-10 grid place-items-center rounded-[100px] bg-[#485695] text-primary',
          showAsk ? 'opacity-100 w-90 h-32' : 'opacity-0 w-0 h-0 overflow-hidden',
        )}
      >
        ask more
      </div>
    </div>
  )
}